/* eslint-disable import/no-default-export */
// @flow

import * as React from 'react';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect';
import DynamicInput from 'dibs-dynamic-input';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
const SHOW_CUSTOM_QUANTITY = 31;
const INPUT_SIZE = 'large';
const NON_NUMERIC_REGEX = /([^\d])/;

const getValOrDefault = (val, defaultVal) => {
    return typeof val === 'undefined' || val === null ? defaultVal : val;
};

const messages = defineMessages({
    customTextPlaceholder: {
        defaultMessage: 'Enter custom quantity',
        id: 'dc.DynamicQuantitySelect.customTextPlaceholder',
    },
    placeholder: {
        defaultMessage: 'Quantity',
        id: 'dc.DynamicQuantitySelect.placeholder',
    },
});
type BasicSelectProps = React.ElementProps<typeof BasicSelect>;
type DynamicQuantitySelectProps = {
    availableQuantity: ?number,
    customInputQuantity?: number,
    dataTn: string,
    disabled?: boolean,
    hasAnimatedPlaceholder?: boolean,
    intl: Object,
    label?: string,
    leftDecorator?: string | React$Element<*>,
    onChange?: (e: SyntheticEvent<*>) => void,
    options: $ElementType<BasicSelectProps, 'options'>,
    renderTextInput?: boolean,
    rightDecorator?: string | React$Element<*>,
    soldAsSet?: boolean,
    size?: $ElementType<BasicSelectProps, 'size'>,
    value: string,
};
type DynamicQuantitySelectState = {
    renderTextInput: boolean,
    selectedQuantity: string,
    showCustomInputQuantity: number,
};
export class DynamicQuantitySelect extends React.Component<
    DynamicQuantitySelectProps,
    DynamicQuantitySelectState
> {
    constructor(props: DynamicQuantitySelectProps) {
        super(props);

        const selectedQuantity = props.value;
        const showCustomInputQuantity = getValOrDefault(
            props.customInputQuantity,
            SHOW_CUSTOM_QUANTITY
        );

        this.onChange = this.onChange.bind(this);

        this.state = {
            renderTextInput: getValOrDefault(
                props.renderTextInput,
                Number(selectedQuantity) >= showCustomInputQuantity
            ),
            selectedQuantity,
            showCustomInputQuantity,
        };
    }

    onChange(e) {
        let value;
        if (typeof e === 'number') {
            value = String(e);
        } else {
            value = e && e.currentTarget && e.currentTarget.value;
        }
        const { onChange } = this.props;
        const renderTextInput =
            this.state.renderTextInput || Number(value) === this.state.showCustomInputQuantity;
        // Reset selected quantity when changing from dropdown -> text
        const shouldResetSelectedQuantity = !this.state.renderTextInput && renderTextInput;

        this.setState({
            renderTextInput,
            selectedQuantity: shouldResetSelectedQuantity || typeof value !== 'string' ? '' : value,
        });

        if (onChange) {
            // Reset quantity to 0 if input is being changed
            if (shouldResetSelectedQuantity) {
                value = 0;
            }
            onChange(value);
        }
    }

    selectedQuantityIsNonNumeric() {
        return NON_NUMERIC_REGEX.test(this.state.selectedQuantity);
    }

    selectedQuantityIsTooHigh() {
        const { availableQuantity } = this.props;
        const { selectedQuantity } = this.state;
        return !!availableQuantity && Number(selectedQuantity) > availableQuantity;
    }

    getValidationErrors() {
        const errors = [];
        const { availableQuantity } = this.props;

        if (this.selectedQuantityIsNonNumeric()) {
            errors.push(
                <FormattedMessage
                    id="dc.DynamicQuantitySelect.errorNonNumericValue"
                    defaultMessage="Please enter a valid quantity"
                />
            );
        }
        if (this.selectedQuantityIsTooHigh()) {
            errors.push(
                <FormattedMessage
                    id="dc.DynamicQuantitySelect.errorQuantityTooHigh"
                    defaultMessage="Only {availableQuantity} available"
                    values={{ availableQuantity }}
                />
            );
        }
        return errors;
    }

    getErrorMessageProp() {
        const validationErrors = this.getValidationErrors();
        return validationErrors.length ? { errorMessage: validationErrors[0] } : null;
    }

    getRightDecorator() {
        const { rightDecorator, soldAsSet } = this.props;
        const { selectedQuantity } = this.state;

        if (rightDecorator) {
            return rightDecorator;
        }

        return soldAsSet ? (
            <FormattedMessage
                id="dc.DynamicQuantitySelect.setsLanguage"
                defaultMessage="{quantity, plural, one {set} other {sets}}"
                values={{ quantity: selectedQuantity }}
            />
        ) : (
            ''
        );
    }

    render() {
        const { renderTextInput, selectedQuantity } = this.state;
        const {
            dataTn,
            disabled,
            hasAnimatedPlaceholder,
            intl,
            label,
            leftDecorator,
            options,
            size,
        } = this.props;

        return (
            <DynamicInput
                {...this.getErrorMessageProp()}
                dataTn={dataTn}
                hasAnimatedPlaceholder={hasAnimatedPlaceholder}
                inputType={renderTextInput ? 'number' : 'select'}
                disabled={getValOrDefault(disabled, false)}
                label={label}
                leftDecorator={leftDecorator}
                placeholder={
                    renderTextInput
                        ? intl.formatMessage(messages.customTextPlaceholder)
                        : intl.formatMessage(messages.placeholder)
                }
                onChange={this.onChange}
                value={selectedQuantity}
                options={options}
                rightDecorator={this.getRightDecorator()}
                size={getValOrDefault(size, INPUT_SIZE)}
            />
        );
    }
}

export default injectIntl(DynamicQuantitySelect);
